import { useSyncExternalStore } from 'react';
import type { PresenceDto } from '@werft/contracts';

/**
 * Präsenz (Paket P9): wer gerade in einem Projekt ist und welche Aufgabe offen hat. Flüchtig – kommt nur über den
 * WebSocket, steht nicht im Query-Cache und überlebt kein Neuladen. Zeilen fragen gezielt „wer hat MICH offen?“ ab
 * und zeichnen nur bei Änderung genau dieser Antwort neu.
 */
class PresenceStore {
  private byProject = new Map<string, PresenceDto[]>();
  private listeners = new Set<() => void>();
  /** Eigene Meldung – wird nach jedem Neuverbinden erneut gesendet */
  mine: { projectId: string | null; taskId: string | null } = { projectId: null, taskId: null };
  meId: string | null = null;

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  };

  set(projectId: string, people: PresenceDto[]) {
    this.byProject.set(projectId, people);
    this.listeners.forEach((l) => l());
  }

  clear(projectId: string) {
    if (this.byProject.delete(projectId)) this.listeners.forEach((l) => l());
  }

  people(projectId: string): PresenceDto[] {
    return this.byProject.get(projectId) ?? NONE;
  }
}

const NONE: PresenceDto[] = [];
export const presence = new PresenceStore();

/** Andere Personen im Projekt (ohne mich) */
export function useOthersInProject(projectId: string): PresenceDto[] {
  const all = useSyncExternalStore(presence.subscribe, () => presence.people(projectId));
  return all.filter((p) => p.userId !== presence.meId);
}

/** Namen der anderen, die diese Aufgabe gerade offen haben – als Zeichenkette, damit nur Änderungen neu zeichnen */
export function useViewers(projectId: string, taskId: string): string {
  return useSyncExternalStore(presence.subscribe, () =>
    presence
      .people(projectId)
      .filter((p) => p.userId !== presence.meId && p.taskIds.includes(taskId))
      .map((p) => p.name)
      .join('\n'),
  );
}
